import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2, ChevronRight, Wrench, ShieldCheck, Truck } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Photoreal, UNBRANDED commercial-vehicle visuals for each master
 * category, generated one-time via Gemini Nano Banana and served from
 * /api/static/landing/. Bake script: backend/scripts/bake_landing_trucks.py
 *
 * Accent colours are intentionally NOT the maker's official brand palette;
 * they are thematic shades so the page feels ownable without infringing.
 */
const STATIC_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/static/landing`;

const BRAND_VISUALS = {
  'Eicher': {
    image: `${STATIC_BASE}/eicher.png`,
    accent: '#E03131',
    glow: 'radial-gradient(circle at 50% 60%, rgba(224,49,49,0.28), transparent 65%)',
    tagline: 'Trucks · Buses · LCV',
    bodyType: 'Heavy & Light Commercial',
  },
  'Mahindra': {
    image: `${STATIC_BASE}/mahindra.png`,
    accent: '#C92A2A',
    glow: 'radial-gradient(circle at 50% 60%, rgba(201,42,42,0.28), transparent 65%)',
    tagline: 'Pickups · Commercial Vehicles',
    bodyType: 'Light Commercial',
  },
  'Swaraj Mazda': {
    image: `${STATIC_BASE}/swaraj-mazda.png`,
    accent: '#0CA678',
    glow: 'radial-gradient(circle at 50% 60%, rgba(12,166,120,0.28), transparent 65%)',
    tagline: 'Light Commercial Vehicles',
    bodyType: 'Light Commercial',
  },
  'Tata': {
    image: `${STATIC_BASE}/tata.png`,
    accent: '#1864AB',
    glow: 'radial-gradient(circle at 50% 60%, rgba(24,100,171,0.32), transparent 65%)',
    tagline: 'Trucks · Buses · LCV',
    bodyType: 'Heavy & Light Commercial',
  },
  'Other Brands': {
    image: `${STATIC_BASE}/other-brands.png`,
    accent: '#94A3B8',
    glow: 'radial-gradient(circle at 50% 60%, rgba(148,163,184,0.26), transparent 65%)',
    tagline: 'Wide compatibility',
    bodyType: 'Multi-Brand',
  },
};

const HERO_IMAGE = `${STATIC_BASE}/hero.png`;

const getVisual = (name) => BRAND_VISUALS[name] || BRAND_VISUALS['Other Brands'];

const BrandLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadCategories(); }, []);

  const loadCategories = async () => {
    try {
      const r = await axios.get(`${API}/master-categories/active`);
      if (r.data?.success && r.data.categories) {
        const cats = r.data.categories;
        if (cats.length === 1) {
          localStorage.setItem('selectedCategory', cats[0].name);
          navigate(`/search/${encodeURIComponent(cats[0].name)}`);
          return;
        }
        setCategories(cats);
      }
    } catch (e) {
      console.error('Failed to load categories:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleBrandClick = (brandName) => {
    localStorage.setItem('selectedCategory', brandName);
    navigate(`/search/${encodeURIComponent(brandName)}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <Loader2 className="w-10 h-10 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="relative min-h-[calc(100vh-4rem)] bg-[#0B1220] text-white flex flex-col">
      {/* ─────────────────────  AMBIENT BACKDROP  ───────────────────── */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div
          className="absolute inset-0 opacity-[0.65]"
          style={{
            background:
              'radial-gradient(1100px 600px at 18% -10%, rgba(232,62,62,0.18), transparent 60%), radial-gradient(900px 500px at 90% 10%, rgba(33,140,255,0.14), transparent 60%), radial-gradient(700px 500px at 50% 95%, rgba(12,166,120,0.10), transparent 60%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />
      </div>

      {/* ──────────────────────────  HERO  ────────────────────────── */}
      <section className="relative pt-20 pb-6 lg:pt-4 lg:pb-4">
        <div className="container mx-auto max-w-[1600px] px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
            {/* Copy column ----------------------------------------- */}
            <div className="lg:col-span-6 xl:col-span-6 relative z-10 flex flex-col">
              <div className="flex items-center gap-4 mb-6 opacity-0 animate-[fadeUp_.7s_ease_.05s_forwards]">
                <img
                  src="/jp_logo.png"
                  alt="Jai Paras"
                  className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg ring-1 ring-white/20 bg-white/5 object-contain"
                />
                <div className="leading-tight">
                  <p className="text-lg sm:text-xl font-bold uppercase tracking-[0.15em] text-white">
                    Jai Paras Spares
                  </p>
                  <p className="text-sm sm:text-base text-white/55 mt-1">
                    OE-Spec Commercial Vehicle Parts · Est. 1985
                  </p>
                </div>
              </div>

              <h1
                className="font-heading font-extrabold text-3xl sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl tracking-tight leading-[1.05] opacity-0 animate-[fadeUp_.8s_ease_.15s_forwards]"
                data-testid="hero-title"
              >
                Built for
                <br />
                <span className="bg-gradient-to-r from-[#FF5A5F] via-[#FF8A5A] to-[#FFC75F] bg-clip-text text-transparent">
                  Indian Highways &amp; Mines.
                </span>
              </h1>

              <p className="mt-5 text-base sm:text-lg text-white/70 max-w-xl opacity-0 animate-[fadeUp_.9s_ease_.25s_forwards]">
                Genuine and OE-spec spare parts for Eicher, Tata, Mahindra and
                Swaraj Mazda commercial vehicles — picked, packed and shipped
                from our Delhi warehouse.
              </p>

              {/* Trust ribbon */}
              <div className="mt-7 flex flex-wrap items-center gap-x-7 gap-y-3 text-sm text-white/65 opacity-0 animate-[fadeUp_1s_ease_.4s_forwards]">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" />
                  Quality-Inspected
                </div>
                <div className="flex items-center gap-2">
                  <Wrench className="w-4 h-4 text-amber-400" />
                  OE-Spec
                </div>
                <div className="flex items-center gap-2">
                  <Truck className="w-4 h-4 text-sky-400" />
                  Pan-India Despatch
                </div>
              </div>

              {user && (
                <p className="mt-6 text-lg sm:text-xl text-white/70">
                  Welcome back, <span className="text-white font-semibold">
                    {user.company_name || user.name || user.mobile}
                  </span>
                </p>
              )}
            </div>

            {/* Hero truck — STATIC in place inside a clean rectangular frame */}
            <div
              className="lg:col-span-6 xl:col-span-6 relative h-[240px] sm:h-[320px] lg:h-auto flex items-center justify-center lg:justify-end lg:pr-1"
              aria-hidden
              data-testid="hero-truck-stage"
            >
              <div
                className="relative w-full max-w-[640px] aspect-[16/10] rounded-2xl overflow-hidden border border-white/10"
                style={{
                  background:
                    'radial-gradient(circle at 50% 60%, rgba(255,90,95,0.18) 0%, rgba(11,18,32,0.0) 65%), linear-gradient(160deg, rgba(255,255,255,0.04) 0%, rgba(11,18,32,0.4) 100%)',
                  boxShadow:
                    '0 30px 60px -20px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04) inset',
                }}
              >
              {/* Ambient warm glow disc behind the truck */}
              <div
                className="absolute inset-0 opacity-0 animate-[heroGlow_1.6s_ease_.3s_forwards]"
                style={{
                  background:
                    'radial-gradient(circle at 55% 60%, rgba(255,90,95,0.32) 0%, rgba(255,140,60,0.12) 38%, transparent 72%)',
                  filter: 'blur(10px)',
                }}
              />
              {/* Cool rim-light wash on the back-right */}
              <div
                className="absolute inset-0 opacity-0 animate-[heroGlow_1.8s_ease_.5s_forwards]"
                style={{
                  background:
                    'radial-gradient(circle at 88% 25%, rgba(80,160,255,0.18) 0%, transparent 55%)',
                  filter: 'blur(12px)',
                  mixBlendMode: 'screen',
                }}
              />
              {/* Soft floor shadow */}
              <div
                className="absolute bottom-3 left-6 right-6 h-10 rounded-full blur-2xl opacity-0 animate-[fadeUp_1s_ease_.7s_forwards]"
                style={{
                  background:
                    'radial-gradient(ellipse at center, rgba(0,0,0,.75), transparent 70%)',
                }}
              />
              {/* The hero truck — fades in then "breathes" with a slow
                  idle float so it doesn't feel like a flat sticker. */}
              <img
                src={HERO_IMAGE}
                alt=""
                draggable={false}
                className="relative w-full h-full object-contain object-center select-none opacity-0 animate-[heroEnter_1.1s_cubic-bezier(.2,.7,.2,1)_.35s_forwards,heroIdle_7s_ease-in-out_1.5s_infinite]"
                style={{
                  filter:
                    'drop-shadow(0 24px 22px rgba(0,0,0,0.5))',
                }}
              />
              {/* Bumper accent stripe (matches the hero gradient) */}
              <div
                className="absolute bottom-1 left-[18%] right-[12%] h-[3px] rounded-full opacity-0 animate-[fadeUp_.8s_ease_1.1s_forwards]"
                style={{
                  background:
                    'linear-gradient(90deg, transparent, #FF3B3F 25%, #FFB347 75%, transparent)',
                  filter: 'blur(0.4px)',
                }}
              />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────── "Choose your vehicle family" CENTER LABEL ──────── */}
      <div className="shrink-0 flex justify-center pb-2 lg:pb-4">
        <p className="text-[11px] sm:text-xs uppercase tracking-[0.24em] text-white/45 opacity-0 animate-[fadeUp_.9s_ease_.6s_forwards]">
          ↓ Choose your vehicle family
        </p>
      </div>

      {/* ─────────────────  MASTER CATEGORY TILES  ─────────────────── */}
      <section className="relative shrink-0 pb-6 lg:pb-5">
        <div className="container mx-auto max-w-[1600px] px-4 md:px-6 lg:px-8">
          <div
            className="grid gap-4 sm:gap-5 md:gap-6 lg:gap-7"
            style={{
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
            }}
          >
            {categories.map((cat, i) => {
              const v = getVisual(cat.name);
              return (
                <button
                  key={cat.name}
                  onClick={() => handleBrandClick(cat.name)}
                  className="group relative text-left overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] hover:border-white/25 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 opacity-0 animate-[fadeUp_.7s_ease_forwards]"
                  style={{ animationDelay: `${0.55 + i * 0.08}s` }}
                  data-testid={`brand-tile-${cat.name.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {/* Truck photo panel ------------------------------- */}
                  <div
                    className="relative aspect-[16/9] overflow-hidden"
                    style={{
                      background:
                        'linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.01) 50%, rgba(11,18,32,0.4) 100%)',
                    }}
                  >
                    {/* Accent glow */}
                    <div
                      className="absolute inset-0"
                      style={{ background: v.glow }}
                    />
                    {/* Faint grid */}
                    <div
                      className="absolute inset-0 opacity-[0.05]"
                      style={{
                        backgroundImage:
                          'linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)',
                        backgroundSize: '24px 24px',
                      }}
                    />
                    {/* Truck — gently lifts on hover for a parallax feel */}
                    <img
                      src={v.image}
                      alt=""
                      loading="lazy"
                      draggable={false}
                      className="absolute inset-0 w-full h-full object-contain object-center transition-transform duration-700 ease-out group-hover:scale-[1.06] group-hover:-translate-y-1 select-none"
                      style={{
                        filter:
                          'drop-shadow(0 20px 18px rgba(0,0,0,0.55))',
                      }}
                    />
                    {/* Bottom scrim so footer text reads cleanly */}
                    <div
                      className="absolute inset-x-0 bottom-0 h-24 pointer-events-none"
                      style={{
                        background:
                          'linear-gradient(180deg, transparent 0%, rgba(11,18,32,.7) 70%, #0B1220 100%)',
                      }}
                    />
                    {/* Body-type pill */}
                    <span
                      className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold backdrop-blur-md bg-black/30 border border-white/15"
                      style={{ color: v.accent }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: v.accent }} />
                      {v.bodyType}
                    </span>
                  </div>

                  {/* Card footer ---------------------------------------- */}
                  <div className="relative -mt-6 px-4 pb-3 pt-2">
                    <div className="flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="text-lg sm:text-xl font-heading font-bold text-white tracking-tight truncate">
                          Suitable for {cat.name}
                        </h3>
                        <p className="text-xs text-white/55 truncate">{v.tagline}</p>
                      </div>
                      <span
                        className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-white/[0.05] border border-white/15 text-white/80 group-hover:bg-white group-hover:text-slate-900 transition-colors duration-300"
                        aria-hidden
                      >
                        <ChevronRight className="w-4 h-4 translate-x-0 group-hover:translate-x-0.5 transition-transform" />
                      </span>
                    </div>

                    {/* Accent underline that grows on hover */}
                    <div className="mt-2 h-[2px] bg-white/[0.07] rounded-full overflow-hidden">
                      <div
                        className="h-full w-0 group-hover:w-full transition-[width] duration-700 ease-out"
                        style={{ background: v.accent }}
                      />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Keyframes — local so we don't touch Tailwind config */}
      <style>{`
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        /* Hero truck simply fades + lifts into place (no drive-in). */
        @keyframes heroEnter {
          0%   { opacity: 0; transform: translateY(14px) scale(0.98); filter: blur(4px); }
          100% { opacity: 1; transform: translateY(0)    scale(1);    filter: blur(0); }
        }
        /* Subtle "breathing" float so the hero feels alive, not flat. */
        @keyframes heroIdle {
          0%, 100% { transform: translateY(0)    scale(1); }
          50%      { transform: translateY(-4px) scale(1.004); }
        }
        @keyframes heroGlow {
          0%   { opacity: 0; transform: scale(0.7); }
          100% { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [data-testid="hero-truck-stage"] img { animation: none !important; opacity: 1 !important; }
        }
      `}</style>
    </div>
  );
};

export default BrandLandingPage;
